import React from 'react';
import { motion } from 'framer-motion';

function ReviewSection() {
  const mainImage = "https://images.unsplash.com/photo-1552581234-26160f608093?q=80&w=2070";

  return (
    <section className="py-24 bg-white overflow-hidden relative">
      {/* Background Decorations */}
      <div className="absolute top-0 left-0 w-full h-full opacity-[0.03] pointer-events-none">
        <img 
          src="https://www.transparenttextures.com/patterns/cubes.png" 
          alt="Geometric pattern for texture" 
          className="w-full h-full"
        />
      </div>

      <div className="max-w-7xl mx-auto px-6">
        {/* SEO: Using 'section' with an aria-label helps screen readers and bots */}
        <div className="bg-slate-900 rounded-[3.5rem] p-12 lg:p-24 relative flex flex-col lg:flex-row items-center gap-16 shadow-2xl overflow-hidden">
          
          {/* Background Gradient Image */}
          <div className="absolute inset-0 z-0 opacity-10">
            <img 
              src="https://images.unsplash.com/photo-1557683316-973673baf926?q=80&w=2029" 
              className="w-full h-full object-cover"
              alt="Deep blue gradient background"
            />
          </div>

          {/* 1. Text Content Area */}
          <div className="lg:w-1/2 text-white z-10">
            <motion.span 
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              className="text-blue-400 font-bold tracking-[0.2em] uppercase text-sm mb-4 block"
            >
              Global Reputation Excellence
            </motion.span>
            
            <motion.h2 
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              className="text-4xl md:text-6xl font-black mb-8 leading-tight"
            >
              Scale Your Authority <span className="text-blue-500">Globally.</span>
            </motion.h2>
            
            <p className="text-gray-400 text-xl mb-10 leading-relaxed font-medium">
              We empower businesses worldwide to dominate search results by building authentic digital authority and social proof that converts.
            </p>

            {/* Feature Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              {[
                { title: 'Global Automation', desc: 'Seamless 24/7 feedback loops.' },
                { title: 'Authority Building', desc: 'Dominating international search.' },
                { title: 'Trust Analytics', desc: 'Real-time performance tracking.' },
                { title: 'Verified Proof', desc: 'Industry-leading social signals.' }
              ].map((item, i) => (
                <motion.div 
                  key={i}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ delay: i * 0.1 }}
                  className="flex flex-col border-l-2 border-blue-600 pl-4"
                >
                  <span className="font-bold text-white text-lg">{item.title}</span>
                  <span className="text-gray-500 text-sm">{item.desc}</span>
                </motion.div>
              ))}
            </div>
          </div>

          {/* 2. Visual/Image Area */}
          <motion.div 
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            className="lg:w-1/2 w-full relative z-10"
          >
            <div className="relative rounded-[2.5rem] overflow-hidden border border-slate-700 shadow-2xl">
              <img 
                src={mainImage} 
                alt="Digital Reputation and Global Authority Management" 
                className="w-full h-[400px] object-cover opacity-80"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-slate-900 via-transparent to-transparent"></div>
              
              {/* Floating Dashboard Card */}
              <div className="absolute top-6 left-6 right-6 bg-slate-800/90 backdrop-blur-md p-6 rounded-3xl border border-white/10">
                <div className="flex justify-between items-center mb-4">
                   <span className="text-gray-300 font-bold text-sm">Global Visibility</span>
                   <span className="bg-green-500/20 text-green-400 px-3 py-1 rounded-full text-[10px] font-black">+124%</span>
                </div>
                <div className="space-y-3">
                  {[90, 75, 85].map((w, i) => (
                    <div key={i} className="h-1.5 bg-slate-700 rounded-full overflow-hidden">
                      <motion.div 
                        initial={{ width: 0 }} 
                        whileInView={{ width: `${w}%` }} 
                        transition={{ duration: 1.5, delay: i*0.2 }}
                        className="h-full bg-blue-500"
                      />
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Floating Star Badge */}
            <motion.div 
              animate={{ y: [0, -15, 0] }}
              transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
              className="absolute -bottom-6 -right-6 bg-blue-600 p-6 rounded-3xl shadow-2xl text-center z-20"
            >
              <div className="flex justify-center gap-1 mb-2">
                {[...Array(5)].map((_, i) => (
                  <span key={i} className="text-yellow-400 text-xs">★</span>
                ))}
              </div>
              <div className="text-3xl font-black text-white">4.9/5</div>
              <div className="text-blue-200 text-[10px] font-bold uppercase tracking-widest">Global Rating</div>
            </motion.div>
          </motion.div>

        </div>
      </div>
    </section>
  );
}

export default ReviewSection;